/** 从「分享文案」里抠出链接（可含中文标题，如 B 站复制的一整段） */
export function extractHttpUrl(text) {
  if (!text) return "";
  const raw = String(text).trim();
  const match = raw.match(/https?:\/\/[^\s"'<>]+/i);
  if (match) {
    return match[0].replace(/[)\]，。；、]+$/u, "");
  }
  return raw;
}

/** 解析第三方视频链接，生成 iframe 嵌入地址（在本站窗口内播放） */
const ALLOWED_EMBED_HOSTS = [
  "player.bilibili.com",
  "v.qq.com",
  "player.youku.com",
  "www.youtube.com",
  "youtube.com",
];

function isAllowedEmbedHost(url) {
  try {
    const hostname = new URL(url).hostname.toLowerCase();
    return ALLOWED_EMBED_HOSTS.some(
      (host) => hostname === host || hostname.endsWith(`.${host}`)
    );
  } catch {
    return false;
  }
}

function guardEmbedUrl(url) {
  if (!url) return "";
  return isAllowedEmbedHost(url) ? url : "";
}
function normalizeEmbedInput(raw) {
  if (!raw) return "";
  let value = extractHttpUrl(String(raw).trim());
  if (!value) return "";

  // 允许直接粘贴 iframe 代码
  const iframeMatch = String(raw).match(/src=["']([^"']+)["']/i);
  if (iframeMatch) {
    value = iframeMatch[1];
  }

  if (value.startsWith("//")) {
    value = `https:${value}`;
  }
  return value;
}

export function resolveEmbedSrc(course) {
  if (course.inline_play_url) {
    return guardEmbedUrl(course.inline_play_url);
  }

  const manual = normalizeEmbedInput(course.embed_url);
  if (manual) {
    return guardEmbedUrl(manual);
  }

  const url = extractHttpUrl(course.video_url || "");
  if (!url) return "";

  const platform = course.platform || "";

  // 哔哩哔哩：普通视频 BV、番剧 ep/ss、旧版 av
  if (platform === "bilibili" || url.includes("bilibili.com") || url.includes("b23.tv")) {
    const bv = url.match(/(BV[\w]+)/i);
    if (bv) {
      return `https://player.bilibili.com/player.html?bvid=${bv[1]}&high_quality=1&autoplay=0&danmaku=0`;
    }
    const ep = url.match(/\/ep(\d+)/i);
    if (ep) {
      return `https://player.bilibili.com/player.html?epid=${ep[1]}&autoplay=0&danmaku=0`;
    }
    const ss = url.match(/\/ss(\d+)/i);
    if (ss) {
      return `https://player.bilibili.com/player.html?ssid=${ss[1]}&autoplay=0&danmaku=0`;
    }
    const av = url.match(/av(\d+)/i);
    if (av) {
      return `https://player.bilibili.com/player.html?aid=${av[1]}&autoplay=0&danmaku=0`;
    }
  }

  // 腾讯视频
  if (platform === "tencent" || url.includes("v.qq.com")) {
    const vidParam = url.match(/[?&]vid=([^&]+)/i);
    if (vidParam) {
      return `https://v.qq.com/txp/iframe/player.html?vid=${vidParam[1]}&autoplay=0`;
    }
    const pageMatch = url.match(/\/x\/page\/([a-zA-Z0-9]+)/i);
    if (pageMatch) {
      return `https://v.qq.com/txp/iframe/player.html?vid=${pageMatch[1]}&autoplay=0`;
    }
  }

  // 优酷
  if (platform === "youku" || url.includes("youku.com")) {
    const idMatch = url.match(/id_([a-zA-Z0-9=+/]+)/i);
    if (idMatch) {
      return `https://player.youku.com/embed/${idMatch[1]}`;
    }
  }

  // YouTube
  if (platform === "youtube" || url.includes("youtube.com") || url.includes("youtu.be")) {
    let videoId = "";
    const watchMatch = url.match(/[?&]v=([^&]+)/);
    if (watchMatch) videoId = watchMatch[1];
    const shortMatch = url.match(/youtu\.be\/([^?&]+)/);
    if (shortMatch) videoId = shortMatch[1];
    const embedMatch = url.match(/youtube\.com\/embed\/([^?&]+)/);
    if (embedMatch) videoId = embedMatch[1];
    if (videoId) {
      return guardEmbedUrl(`https://www.youtube.com/embed/${videoId}`);
    }
  }

  return "";
}

export function canPlayInline(course) {
  return Boolean(resolveEmbedSrc(course));
}

export const AUDIENCE_LABELS = {
  all: "全部人群",
  single: "面向单身",
  married: "面向已婚",
};

export const ACCESS_LEVEL_LABELS = {
  public: "所有人可看",
  member: "登录会员",
  paid: "付费会员",
};
